import React, { useEffect, useMemo, useRef } from "react";
import { PLANES, DYNAMIC_PACKAGES, useSearch } from "../../../contexts/SearchContext";
import { normalizeAvailableFlights } from "../adapters/flightOfferAdapter";
import { buildPlanesSearchSummary } from "../adapters/flightSearchDisplayAdapter";
import { shouldOpenBookingFlowAfterFlightSearch } from "../flightBookingFlowAutoAdvance";
import { computeFlightBookingSearchGate } from "../flightBookingSearchGate";
import { useBookingFlow } from "./hooks/useBookingFlow";
import { peekMainFlightSearchEditorRequest } from "../../../features/searchOffer/searchOfferEditSearchHandshake";

const FLIGHT_TABS = [PLANES, DYNAMIC_PACKAGES];
const DEBUG_ENDPOINT =
  "http://127.0.0.1:7626/ingest/ca460569-320b-4741-be20-10cc54aa7608";

function postDebugLog({ runId, hypothesisId, location, message, data }) {
  return;
}

/**
 * מקדם ל־results אחרי submitSearch מהשורה העליונה (בלי כרטיס חיפוש ב-wizard).
 */
export default function FlightSearchToResultsBridge() {
  const {
    loading,
    searchTab,
    isSearched,
    availableFlights,
    flightTripType,
    parallelRoundTripFlightSearchCompleted,
    roundTripFlightResults,
    outboundOneWayFlightResults,
    inboundOneWayFlightResults,
    origin,
    location,
    calendarDateArrival,
    calendarDateLeave,
    roomInfo,
    planesFlightSearchOutcome,
  } = useSearch();
  const { openResultsAfterPlanesSearch, currentStep, selectedFlightOffer } =
    useBookingFlow();

  const awaitingCompletionRef = useRef(false);
  const prevLoadingRef = useRef(loading);
  const availableFlightsRef = useRef(availableFlights);
  const flightTripTypeRef = useRef(flightTripType);
  const planesFlightSearchOutcomeRef = useRef(planesFlightSearchOutcome);
  const currentStepRef = useRef(currentStep);
  const selectedFlightOfferRef = useRef(selectedFlightOffer);
  flightTripTypeRef.current = flightTripType;
  planesFlightSearchOutcomeRef.current = planesFlightSearchOutcome;
  currentStepRef.current = currentStep;
  selectedFlightOfferRef.current = selectedFlightOffer;
  const searchGateRef = useRef(
    computeFlightBookingSearchGate({
      flightTripType,
      parallelRoundTripFlightSearchCompleted,
      roundTripFlightResults,
      outboundOneWayFlightResults,
      inboundOneWayFlightResults,
    })
  );

  const searchGate = useMemo(
    () =>
      computeFlightBookingSearchGate({
        flightTripType,
        parallelRoundTripFlightSearchCompleted,
        roundTripFlightResults,
        outboundOneWayFlightResults,
        inboundOneWayFlightResults,
      }),
    [
      flightTripType,
      parallelRoundTripFlightSearchCompleted,
      roundTripFlightResults,
      outboundOneWayFlightResults,
      inboundOneWayFlightResults,
    ]
  );

  availableFlightsRef.current = availableFlights;
  searchGateRef.current = searchGate;

  useEffect(() => {
    const pathNow =
      typeof window !== "undefined" ? window.location.pathname || "" : "";
    const prev = prevLoadingRef.current;
    prevLoadingRef.current = loading;
    /**
     * Do not navigate to /flights/results on loading start.
     * That remounts PriceOffer (sibling route) mid-request and the results
     * page then renders the "אין תוצאות עדיין" empty state. Stay on search,
     * show the in-place skeleton, and advance only after deals land.
     */
    if (pathNow.includes("/flights/results")) {
      awaitingCompletionRef.current = false;
      return;
    }
    if (currentStep !== "search") {
      awaitingCompletionRef.current = false;
      return;
    }
    if (!FLIGHT_TABS.includes(searchTab)) {
      awaitingCompletionRef.current = false;
      return;
    }
    if (selectedFlightOfferRef.current != null) {
      awaitingCompletionRef.current = false;
      return;
    }
    const stepNow = currentStepRef.current;
    if (!prev && loading && (stepNow === "search" || currentStep === "search")) {
      awaitingCompletionRef.current = true;
      const summary = buildPlanesSearchSummary({
        origin,
        location,
        calendarDateArrival,
        calendarDateLeave,
        roomInfo,
      });
      console.log("[FlightBookingFlow] flight search submitted (awaiting completion)", {
        searchTab,
        flightTripType,
        risingEdge: true,
        summary,
      });
    }
  }, [
    loading,
    searchTab,
    currentStep,
    flightTripType,
    origin,
    location,
    calendarDateArrival,
    calendarDateLeave,
    roomInfo,
  ]);

  /**
   * Fallback guard: if loading completion transition was missed due render timing,
   * but search is already done and results/empty state are ready — force results step.
   */
  useEffect(() => {
    const pathNow =
      typeof window !== "undefined" ? window.location.pathname || "" : "";
    if (pathNow.includes("/flights/results")) return;
    if (currentStep !== "search") return;
    if (peekMainFlightSearchEditorRequest()) return;
    // #region agent log
    postDebugLog({
      runId: "run2",
      hypothesisId: "H2",
      location: "FlightSearchToResultsBridge.jsx:151",
      message: "[FLIGHT_BRIDGE_FALLBACK_EVAL]",
      data: {
        loading,
        isSearched,
        searchTab,
        currentStep,
        pathname: window.location.pathname,
      },
    });
    // #endregion
    if (loading) return;
    if (!isSearched) return;
    if (!FLIGHT_TABS.includes(searchTab)) return;
    if (currentStep !== "search") return;
    if (selectedFlightOffer != null) return;
    const open = shouldOpenBookingFlowAfterFlightSearch({
      availableFlights,
      searchGate,
      flightTripType,
      planesFlightSearchOutcome,
    });
    if (!open) return;
    // #region agent log
    postDebugLog({
      runId: "run1",
      hypothesisId: "H2",
      location: "FlightSearchToResultsBridge.jsx:121",
      message: "[FLIGHT_RESULTS_BACK_NAV_SOURCE]",
      data: {
        sourceFile: "FlightSearchToResultsBridge.jsx",
        action: "openResultsAfterPlanesSearch",
        fromStep: currentStep,
        targetStep: "results",
        pathnameBefore: window.location.pathname,
        nextPath: null,
        reason: "fallback_guard_after_search_completion",
      },
    });
    // #endregion
    console.log("[FLIGHT_SEARCH_DEBUG] forcing results step fallback", {
      loading,
      searchTab,
      currentStep,
      dealsCount: normalizeAvailableFlights(availableFlights).length,
      planesFlightSearchOutcome,
      parallelOk: searchGate?.parallelOk,
    });
    openResultsAfterPlanesSearch();
  }, [
    loading,
    isSearched,
    searchTab,
    currentStep,
    availableFlights,
    searchGate,
    flightTripType,
    planesFlightSearchOutcome,
    openResultsAfterPlanesSearch,
  ]);

  useEffect(() => {
    const pathNow =
      typeof window !== "undefined" ? window.location.pathname || "" : "";
    if (pathNow.includes("/flights/results")) {
      awaitingCompletionRef.current = false;
      return;
    }
    if (currentStep !== "search") {
      awaitingCompletionRef.current = false;
      return;
    }
    if (!awaitingCompletionRef.current) return;
    if (loading) return;
    if (!FLIGHT_TABS.includes(searchTab)) {
      awaitingCompletionRef.current = false;
      return;
    }
    if (currentStepRef.current !== "search") {
      awaitingCompletionRef.current = false;
      return;
    }
    if (selectedFlightOfferRef.current != null) {
      awaitingCompletionRef.current = false;
      return;
    }

    const tryAdvance = (label) => {
      if (!awaitingCompletionRef.current) return false;
      const stepNow = currentStepRef.current;
      // #region agent log
      if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
        console.log("[FLIGHT_LOOP_AUDIT]", {
          runId: "run3",
          hypothesisId: "H6",
          source: "FlightSearchToResultsBridge",
          action: "tryAdvance_enter",
          label: label || "immediate",
          stepNow,
          currentStepSnapshot: currentStep,
          loading,
          isSearched,
          pathname: window.location.pathname,
        });
      }
      // #endregion
      // #region agent log
      postDebugLog({
        runId: "run2",
        hypothesisId: "H3",
        location: "FlightSearchToResultsBridge.jsx:223",
        message: "[FLIGHT_BRIDGE_TRY_ADVANCE]",
        data: {
          action: "tryAdvance_enter",
          label: label || "immediate",
          stepNow,
          currentStepSnapshot: currentStep,
          awaitingCompletion: awaitingCompletionRef.current,
          pathname: window.location.pathname,
        },
      });
      // #endregion
      if (stepNow !== "search") {
        awaitingCompletionRef.current = false;
        // #region agent log
        postDebugLog({
          runId: "run1",
          hypothesisId: "H1",
          location: "FlightSearchToResultsBridge.jsx:163",
          message: "[FLIGHT_RESULTS_BACK_NAV_SOURCE]",
          data: {
            sourceFile: "FlightSearchToResultsBridge.jsx",
            action: "skip_open_results",
            fromStep: stepNow,
            targetStep: "results",
            pathnameBefore: window.location.pathname,
            nextPath: null,
            reason: "awaiting_completion_but_step_already_advanced",
          },
        });
        // #endregion
        return false;
      }
      if (selectedFlightOfferRef.current != null) {
        awaitingCompletionRef.current = false;
        return false;
      }
      const open = shouldOpenBookingFlowAfterFlightSearch({
        availableFlights: availableFlightsRef.current,
        searchGate: searchGateRef.current,
        flightTripType: flightTripTypeRef.current,
        planesFlightSearchOutcome: planesFlightSearchOutcomeRef.current,
      });
      if (!open) return false;
      awaitingCompletionRef.current = false;
      // #region agent log
      if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
        console.log("[FLIGHT_LOOP_AUDIT]", {
          runId: "run3",
          hypothesisId: "H6",
          source: "FlightSearchToResultsBridge",
          action: "open_results_from_tryAdvance",
          label: label || "immediate",
          stepNow,
          pathname: window.location.pathname,
        });
      }
      // #endregion
      // #region agent log
      postDebugLog({
        runId: "run1",
        hypothesisId: "H3",
        location: "FlightSearchToResultsBridge.jsx:160",
        message: "[FLIGHT_RESULTS_BACK_NAV_SOURCE]",
        data: {
          sourceFile: "FlightSearchToResultsBridge.jsx",
          action: "openResultsAfterPlanesSearch",
          fromStep: currentStep,
          targetStep: "results",
          pathnameBefore: window.location.pathname,
          nextPath: null,
          reason: `awaiting_completion_tryAdvance${label ? `_${label}` : ""}`,
        },
      });
      // #endregion
      openResultsAfterPlanesSearch();
      console.log(
        `[FlightBookingFlow] switched to results view after flight search${label ? ` (${label})` : ""}`,
        {
          fromStep: currentStep,
          dealsCount: normalizeAvailableFlights(availableFlightsRef.current).length,
          parallelOk: searchGateRef.current.parallelOk,
          rtCount: searchGateRef.current.rtCount,
          outboundCount: searchGateRef.current.outboundCount,
          inboundCount: searchGateRef.current.inboundCount,
        }
      );
      return true;
    };

    if (tryAdvance("")) return undefined;

    const tid = window.setTimeout(() => {
      if (tryAdvance("deferred tick")) return;
      if (!awaitingCompletionRef.current) return;
      awaitingCompletionRef.current = false;
      console.log(
        "[FlightBookingFlow] flight search finished — staying on current step (no browse-ready results)",
        {
          currentStep,
          dealsCount: normalizeAvailableFlights(availableFlightsRef.current).length,
          parallelOk: searchGateRef.current.parallelOk,
        }
      );
    }, 280);

    return () => clearTimeout(tid);
  }, [
    loading,
    searchTab,
    availableFlights,
    searchGate,
    flightTripType,
    planesFlightSearchOutcome,
    openResultsAfterPlanesSearch,
    currentStep,
    selectedFlightOffer,
  ]);

  return null;
}
